import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router'
import {
  Button,
  Alert,
  ActionIcon,
  Modal,
  Drawer,
  FocusTrap,
  Progress,
} from '@mantine/core'
import { HtmlTypography, Typography } from '../common/typography'
import { useQuizContext } from './QuizAttemptLayout'
import {
  useSubmitAnswer,
  useFinishAttempt,
  getAttemptResult,
  type FinishAttemptResponse,
} from './api'
import { IconBulb, IconChevronLeft } from '@tabler/icons-react'
import CircularTimer from './components/CircularTimer'
import classes from './QuestionGroup.module.css'
import Header from '../common/components/header/Header'
import Meta from '../common/Meta'
import { useMediaQuery, useDisclosure } from '@mantine/hooks'
import clsx from 'clsx'

export default function QuestionGroup() {
  const { questionGroupId } = useParams<{ questionGroupId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const {
    courseDetails,
    attemptId,
    progress,
    updateProgress,
    timerEnabled,
    remainingSeconds,
    questionGroups,
  } = useQuizContext()

  const submitAnswerMutation = useSubmitAnswer()
  const finishAttemptMutation = useFinishAttempt(courseDetails.quiz_id)

  const currentGroupId = Number(questionGroupId)
  const currentGroup = questionGroups.find(
    (group) => group.question_group_id === currentGroupId
  )
  const currentGroupIndex = questionGroups.findIndex(
    (group) => group.question_group_id === currentGroupId
  )

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    if (progress.currentGroupId === currentGroupId) {
      return progress.currentQuestionIndex
    }

    const answeredIds =
      courseDetails.in_progress_attempt?.answered_question_ids || []
    const firstUnansweredIndex = currentGroup!.questions.findIndex(
      (q) => !answeredIds.includes(q.question_id)
    )

    return firstUnansweredIndex !== -1 ? firstUnansweredIndex : 0
  })
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([])
  const [hintOpen, { open: openHint, close: closeHint }] = useDisclosure(false)
  const [answerSubmitted, setAnswerSubmitted] = useState(false)
  const [correctAnswerIds, setCorrectAnswerIds] = useState<number[]>([])
  const [quizFinishResult, setQuizFinishResult] =
    useState<FinishAttemptResponse | null>(null)

  const hasAutoFinished = useRef(false)

  useEffect(() => {
    if (
      timerEnabled &&
      remainingSeconds !== null &&
      remainingSeconds <= 0 &&
      !hasAutoFinished.current
    ) {
      hasAutoFinished.current = true

      getAttemptResult(attemptId)
        .then((attemptStatus) => {
          if (attemptStatus.completed_at) {
            navigate(`/learning/course/${courseDetails.quiz_id}/result`, {
              state: { result: attemptStatus, attemptId },
            })
          } else {
            return finishAttemptMutation
              .mutateAsync(attemptId)
              .then((result) => {
                navigate(`/learning/course/${courseDetails.quiz_id}/result`, {
                  state: { result, attemptId },
                })
              })
          }
        })
        .catch((error) => {
          console.error('Failed to auto-finish quiz:', error)
          navigate(`/learning/course/${courseDetails.quiz_id}/result`, {
            state: { attemptId },
          })
        })
    }
  }, [
    timerEnabled,
    remainingSeconds,
    attemptId,
    courseDetails.quiz_id,
    finishAttemptMutation,
    navigate,
  ])

  if (!currentGroup) {
    return (
      <div className={classes.container}>
        <Alert color="red" title="Error">
          Question group not found
        </Alert>
      </div>
    )
  }

  const currentQuestion = currentGroup.questions[currentQuestionIndex]

  if (!currentQuestion) {
    return (
      <div className={classes.container}>
        <Alert color="red" title="Error">
          Question not found
        </Alert>
      </div>
    )
  }

  const handleAnswerToggle = (answerId: number) => {
    if (answerSubmitted) return

    setSelectedAnswers((prev) => {
      const pickCount = currentQuestion.pick_count ?? 1

      if (prev.includes(answerId)) {
        return prev.filter((id) => id !== answerId)
      }

      if (pickCount === 1) {
        return [answerId]
      }

      if (prev.length >= pickCount) {
        return prev
      }

      return [...prev, answerId]
    })
  }

  const handleSubmitAnswer = async () => {
    if (selectedAnswers.length === 0) return

    if (answerSubmitted) {
      handleAdvance(quizFinishResult)
      return
    }

    const isLastQuestion =
      currentQuestionIndex === currentGroup.questions.length - 1 &&
      currentGroupIndex === questionGroups.length - 1

    try {
      const result = await submitAnswerMutation.mutateAsync({
        attempt_id: attemptId,
        question_id: currentQuestion.question_id,
        selected_answer_ids: selectedAnswers,
      })

      updateProgress({
        currentGroupId,
        currentQuestionIndex,
        answeredQuestions: [
          ...progress.answeredQuestions,
          currentQuestion.question_id,
        ],
      })

      let finishResult: FinishAttemptResponse | null = null
      if (isLastQuestion) {
        finishResult = await finishAttemptMutation.mutateAsync(attemptId)
        setQuizFinishResult(finishResult)
      }

      if (courseDetails.show_correct_answers) {
        setAnswerSubmitted(true)
        setCorrectAnswerIds(result.correct_answer_ids)
      } else {
        handleAdvance(finishResult)
      }
    } catch (error) {
      console.error('Failed to submit answer:', error)
    }
  }

  const handleAdvance = (finishResult: FinishAttemptResponse | null) => {
    setSelectedAnswers([])
    setAnswerSubmitted(false)
    setCorrectAnswerIds([])

    if (currentQuestionIndex < currentGroup.questions.length - 1) {
      const nextQuestionIndex = currentQuestionIndex + 1
      setCurrentQuestionIndex(nextQuestionIndex)
      updateProgress({
        currentQuestionIndex: nextQuestionIndex,
      })
    } else {
      if (currentGroupIndex < questionGroups.length - 1) {
        const nextGroup = questionGroups[currentGroupIndex + 1]
        updateProgress({
          currentGroupId: nextGroup.question_group_id,
          currentQuestionIndex: 0,
        })
        navigate(
          `/learning/course/${courseDetails.quiz_id}/quiz/${attemptId}/question-group/${nextGroup.question_group_id}`
        )
      } else {
        finishQuiz(finishResult)
      }
    }
  }

  const finishQuiz = async (finishResult: FinishAttemptResponse | null) => {
    try {
      if (finishResult) {
        navigate(`/learning/course/${courseDetails.quiz_id}/result`, {
          state: { result: finishResult, attemptId },
        })
        return
      }

      const result = await finishAttemptMutation.mutateAsync(attemptId)

      navigate(`/learning/course/${courseDetails.quiz_id}/result`, {
        state: { result, attemptId },
      })
    } catch (error) {
      console.error('Failed to finish quiz:', error)
    }
  }

  const isSubmitDisabled =
    (selectedAnswers.length === 0 && !answerSubmitted) ||
    submitAnswerMutation.isPending

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        {isDesktop && (
          <IconChevronLeft
            onClick={() =>
              navigate(location.state?.from?.pathname || '/learning')
            }
            className={classes.backIcon}
          />
        )}
        <Header
          title="Learning XP"
          back={location.state?.from?.pathname || '/learning'}
        />
      </div>
      <Meta title="Learning XP" />

      <div
        className={clsx(classes.questionContainer, {
          [classes.questionContainerMobile]: !isDesktop,
        })}
      >
        <div className={classes.questionHeader}>
          <div className={classes.questionHeaderTop}>
            <Typography lh="150">
              {currentQuestionIndex + 1} / {currentGroup.questions.length}
            </Typography>

            <Progress
              value={
                ((currentQuestionIndex + 1) / currentGroup.questions.length) *
                100
              }
              size="sm"
              radius="xl"
              classNames={{
                root: classes.progressRoot,
                section: classes.progressSection,
              }}
            />

            {timerEnabled &&
              remainingSeconds !== null &&
              courseDetails.duration_seconds && (
                <CircularTimer
                  remainingSeconds={remainingSeconds}
                  totalSeconds={courseDetails.duration_seconds}
                />
              )}
          </div>
        </div>
        <div className={classes.questionWrapper}>
          <HtmlTypography fs="xl" fw={700} lh="125">
            {currentQuestion.question_text}
          </HtmlTypography>

          <div className={classes.answersContainer}>
            {currentQuestion.answers.map((answer) => {
              const isSelected = selectedAnswers.includes(answer.answer_id)
              const isCorrect =
                answerSubmitted && correctAnswerIds.includes(answer.answer_id)
              const isIncorrect =
                answerSubmitted &&
                isSelected &&
                !correctAnswerIds.includes(answer.answer_id)

              return (
                <div
                  key={answer.answer_id}
                  className={classes.answer}
                  onClick={() => handleAnswerToggle(answer.answer_id)}
                  data-selected={isSelected}
                  data-correct={isCorrect}
                  data-incorrect={isIncorrect}
                >
                  <Typography>{answer.answer_text}</Typography>
                </div>
              )
            })}
          </div>
        </div>

        <div className={classes.buttonRow}>
          {currentQuestion.hint_text && (
            <ActionIcon
              variant="outline"
              aria-label="Hint"
              className={classes.actionIcon}
              onClick={openHint}
              data-active={hintOpen}
            >
              <IconBulb />
            </ActionIcon>
          )}
          <Button
            className={classes.submitButton}
            onClick={handleSubmitAnswer}
            disabled={isSubmitDisabled}
            loading={
              submitAnswerMutation.isPending || finishAttemptMutation.isPending
            }
          >
            <Typography
              fs="sm"
              fc={{ light: 'white', dark: 'white' }}
              lh="115"
              tt="uppercase"
            >
              {answerSubmitted &&
              !(
                currentQuestionIndex === currentGroup.questions.length - 1 &&
                currentGroupIndex === questionGroups.length - 1
              )
                ? 'Next'
                : currentQuestionIndex === currentGroup.questions.length - 1 &&
                    currentGroupIndex === questionGroups.length - 1 &&
                    (answerSubmitted || !courseDetails.show_correct_answers)
                  ? 'Finish'
                  : 'Confirm'}
            </Typography>
          </Button>
        </div>
      </div>

      {isDesktop ? (
        <Modal.Root
          opened={hintOpen}
          onClose={closeHint}
          centered
          size="sm"
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
          classNames={{ header: classes.header, body: classes.body }}
        >
          <Modal.Overlay backgroundOpacity={0.3} blur={2} />
          <Modal.Content>
            <FocusTrap.InitialFocus />
            <Modal.Header>
              <Modal.Title>
                <Typography fw={500} lh="125">
                  Hint
                </Typography>
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              <Typography lh="150">{currentQuestion.hint_text}</Typography>
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      ) : (
        <Drawer.Root
          opened={hintOpen}
          onClose={closeHint}
          position="bottom"
          classNames={{
            header: classes.header,
            body: classes.body,
            content: classes.hintContent,
          }}
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
        >
          <Drawer.Overlay backgroundOpacity={0.3} blur={2} />
          <Drawer.Content>
            <FocusTrap.InitialFocus />
            <Drawer.Header>
              <Drawer.Title>
                <Typography fs="lg" fw={500} lh="125">
                  Hint
                </Typography>
              </Drawer.Title>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body>
              <Typography lh="150">{currentQuestion.hint_text}</Typography>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </div>
  )
}
